import React, { useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ReferenceLine,
} from 'recharts';
import { useTheme } from '../contexts/ThemeContext';
import { run, isAbortError } from '../lib/worker-pool';
import { analysisApi } from '../lib/api';
import { gridFromResult } from '../lib/grid-ops';
import { collectSoundings, profileToCsv, MAX_PROFILE_SAMPLES } from '../lib/grid-profile';
import useTerrainSettings from '../hooks/useTerrainSettings';
import { DEM_PROFILE_SERIES, mergeDemProfile, terrainApi } from '../lib/terrain-api';

const SERIES_COLORS = ['#00d4ff', '#34d399', '#fbbf24', '#f87171', '#a78bfa', '#fb923c'];
const MAX_GRIDS = 6;
const CHART_W = 336;
const CHART_H = 230;

const lineMetres = (coords) => {
  const midLat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
  const mLon = 111320 * Math.cos((midLat * Math.PI) / 180);
  let d = 0;
  for (let i = 1; i < coords.length; i++) d += Math.hypot((coords[i][0] - coords[i - 1][0]) * mLon, (coords[i][1] - coords[i - 1][1]) * 110540);
  return d;
};

/**
 * Y-axis domain that applies vertical exaggeration: at VE 1 one value unit spans the
 * same number of pixels as one metre along X; VE n makes it n times taller. The data
 * range is centred in the window.
 */
export function exaggeratedDomain(profile, ve) {
  let lo = Infinity, hi = -Infinity;
  for (const s of profile.samples) {
    for (const n of profile.series) {
      const v = s.values[n];
      if (v === null || v === undefined) continue;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
  }
  if (!isFinite(lo)) return ['auto', 'auto'];
  const span = Math.max(profile.length_m, 1) * (CHART_H / CHART_W) / Math.max(ve, 0.01);
  const mid = (lo + hi) / 2;
  return [mid - span / 2, mid + span / 2];
}

/**
 * GridProfilePanel — sample one or more grids along a drawn polyline (Wave 3.5), and the
 * project DEM as an "Elevation (DEM)" series when the project has one (POST /dem/profile,
 * same path sampling, merged index by index; docs/DEM_TERRAIN.md).
 *
 * Props:
 *   gridLayers    - gridding result layers [{ id, name, geojson (with metadata.grid) }]
 *   pointLayers   - layers whose point features are candidate soundings
 *   drawnFeatures - Terra Draw snapshot (the last LineString is the profile line)
 *   setDrawMode(mode), clearDrawn()
 *   onClose()
 */
export default function GridProfilePanel({ gridLayers = [], pointLayers = [], drawnFeatures = [], setDrawMode, clearDrawn, onClose }) {
  const { C: themeC } = useTheme();
  const C = { ...themeC, amber: themeC.warnText };
  const usable = useMemo(() => gridLayers.filter(l => gridFromResult(l.geojson)), [gridLayers]);
  const [picked, setPicked] = useState([]);
  const [step, setStep] = useState('25');
  const [ve, setVe] = useState('1');
  const [buffer, setBuffer] = useState('100');
  const [withSoundings, setWithSoundings] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const terrain = useTerrainSettings();
  const demId = terrain.demDatasetId;
  const [withDem, setWithDem] = useState(true);
  const useDem = !!demId && withDem;

  const line = useMemo(() => {
    const lines = drawnFeatures.filter(f => f.geometry?.type === 'LineString' && f.geometry.coordinates?.length >= 2);
    return lines.length ? lines[lines.length - 1].geometry.coordinates : null;
  }, [drawnFeatures]);

  const toggle = (id) => setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : (p.length < MAX_GRIDS ? [...p, id] : p)));

  const stepM = parseFloat(step);
  const lengthM = line ? lineMetres(line) : 0;
  const stepOk = stepM > 0 && lengthM / stepM + 1 <= MAX_PROFILE_SAMPLES;
  const canRun = !!line && (picked.length > 0 || useDem) && stepOk && !busy;

  const compute = async () => {
    setError(null);
    setBusy(true);
    try {
      const chosen = usable.filter(l => picked.includes(l.id));
      const grids = chosen.map(l => ({ name: l.name, ...gridFromResult(l.geojson) }));
      const points = withSoundings ? collectSoundings(pointLayers) : [];
      const req = { grids, line, step: stepM, points, buffer: parseFloat(buffer) || 100 };
      let result = null;
      if (grids.length) {
        try {
          result = await run('gridProfile', req, { label: 'Grid profile' });
        } catch (err) {
          if (isAbortError(err)) throw err;
          const res = await analysisApi.gridProfile(req); // worker unavailable: ask the server
          result = res.data;
        }
      }
      if (useDem) {
        const dem = await terrainApi.profile(demId, { line, step: stepM, points, buffer: req.buffer });
        result = mergeDemProfile(result, dem.data);
      }
      setProfile(result);
    } catch (err) {
      if (!isAbortError(err)) setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const chartData = useMemo(() => (profile
    ? profile.samples.map(s => ({ dist: s.dist, ...Object.fromEntries(profile.series.map((n, i) => [`s${i}`, s.values[n]])) }))
    : []), [profile]);
  const domain = useMemo(() => (profile ? exaggeratedDomain(profile, parseFloat(ve) || 1) : ['auto', 'auto']), [profile, ve]);

  const exportCsv = () => {
    const blob = new Blob([profileToCsv(profile)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'grid-profile.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const lbl = { fontSize: 10, color: C.text2, display: 'block', marginBottom: 3 };
  const inp = { width: '100%', boxSizing: 'border-box', padding: '5px 8px', borderRadius: 6, border: `1px solid ${C.border}`, background: C.bg3, color: C.text0, fontSize: 11 };
  const btn = (primary) => ({
    padding: '7px 10px', borderRadius: 8, border: primary ? 'none' : `1px solid ${C.border}`, fontSize: 11, fontWeight: 600, cursor: 'pointer',
    background: primary ? 'linear-gradient(135deg,#00b8de,#3b82f6)' : C.bg3, color: primary ? '#fff' : C.text1,
  });

  return (
    <div style={{ width: 360, background: C.bg2, borderLeft: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflow: 'auto', flexShrink: 0 }}>
      <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.text0 }}>📈 Grid Profile</span>
        <button onClick={onClose} aria-label="Close grid profile" style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 14 }}>✕</button>
      </div>

      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {usable.length === 0 && !demId ? (
          <div style={{ fontSize: 11, color: C.text3, lineHeight: 1.6 }}>
            Run Gridding first — the profile samples gridding result layers (or set a project DEM).
          </div>
        ) : (
          <>
            {demId && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.text1 }} data-testid="profile-dem">
                <input type="checkbox" checked={withDem} onChange={e => setWithDem(e.target.checked)} />
                {DEM_PROFILE_SERIES}{terrain.dem?.name ? ` — ${terrain.dem.name}` : ''}
              </label>
            )}
            <div>
              <label style={lbl}>Grids (up to {MAX_GRIDS})</label>
              {usable.map(l => (
                <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.text1, padding: '2px 0' }}>
                  <input type="checkbox" checked={picked.includes(l.id)} onChange={() => toggle(l.id)} />
                  {l.name}
                </label>
              ))}
            </div>

            <div>
              <label style={lbl}>Profile line</label>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <button style={btn(false)} onClick={() => setDrawMode && setDrawMode('line')}>✏️ Draw line</button>
                <button style={btn(false)} onClick={() => clearDrawn && clearDrawn()}>Clear</button>
              </div>
              <div style={{ fontSize: 10, marginTop: 4, color: line ? C.emerald : C.amber }}>
                {line ? `✓ ${line.length} vertices, ${Math.round(lengthM)} m` : 'Draw a polyline on the map (double-click to finish)'}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              <div>
                <label style={lbl}>Step (m)</label>
                <input type="number" min="0.1" step="any" value={step} onChange={e => setStep(e.target.value)} style={inp} />
              </div>
              <div>
                <label style={lbl}>Vert. exag. (×)</label>
                <input type="number" min="0.01" step="any" value={ve} onChange={e => setVe(e.target.value)} style={inp} />
              </div>
              <div>
                <label style={lbl}>Buffer (m)</label>
                <input type="number" min="1" step="any" value={buffer} onChange={e => setBuffer(e.target.value)} style={inp} />
              </div>
            </div>
            {!stepOk && line && <div style={{ fontSize: 10, color: C.red }}>Step too small: at most {MAX_PROFILE_SAMPLES} samples.</div>}
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.text1 }}>
              <input type="checkbox" checked={withSoundings} onChange={e => setWithSoundings(e.target.checked)} />
              Mark soundings / points within the buffer
            </label>

            <button style={{ ...btn(true), opacity: canRun ? 1 : 0.5 }} disabled={!canRun} onClick={compute}>
              {busy ? 'Sampling…' : 'Compute profile'}
            </button>
          </>
        )}

        {error && <div style={{ fontSize: 11, color: C.red }}>{error}</div>}

        {profile && (
          <>
            <div style={{ fontSize: 10, color: C.text2 }}>
              {Math.round(profile.length_m)} m · {profile.samples.length} samples · VE ×{parseFloat(ve) || 1}
            </div>
            <LineChart width={CHART_W} height={CHART_H} data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis type="number" dataKey="dist" domain={[0, 'dataMax']} tick={{ fontSize: 9, fill: C.text3 }} tickFormatter={(v) => `${Math.round(v)}`} />
              <YAxis type="number" domain={domain} allowDataOverflow tick={{ fontSize: 9, fill: C.text3 }} tickFormatter={(v) => (Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(1))} width={44} />
              <ChartTooltip
                contentStyle={{ background: C.bg3, border: `1px solid ${C.border}`, fontSize: 10 }}
                labelFormatter={(d) => `${d} m`}
                formatter={(v, key) => [v === null || v === undefined ? 'no data' : v, profile.series[parseInt(String(key).slice(1), 10)] || key]}
              />
              <Legend wrapperStyle={{ fontSize: 9 }} formatter={(key) => profile.series[parseInt(String(key).slice(1), 10)] || key} />
              {profile.series.map((n, i) => (
                <Line key={n} dataKey={`s${i}`} name={`s${i}`} type="monotone" stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                  strokeWidth={1.6} dot={false} connectNulls={false} isAnimationActive={false} />
              ))}
              {profile.soundings.map((s, i) => (
                <ReferenceLine key={`snd-${i}`} x={s.dist} stroke={C.amber} strokeDasharray="2 3"
                  label={{ value: String(s.label || '▼').slice(0, 10), position: 'insideTop', fontSize: 8, fill: C.amber, angle: -90 }} />
              ))}
            </LineChart>

            {profile.soundings.length > 0 && (
              <div style={{ fontSize: 10, color: C.text2, lineHeight: 1.6, maxHeight: 90, overflow: 'auto' }}>
                {profile.soundings.map((s, i) => (
                  <div key={i}>▼ {s.label || s.layer || 'point'} · {s.dist} m along · {s.offset} m off{s.value !== null && s.value !== undefined ? ` · ${s.value}` : ''}</div>
                ))}
              </div>
            )}
            {withSoundings && profile.soundings.length === 0 && (
              <div style={{ fontSize: 10, color: C.text2 }}>No points within the buffer.</div>
            )}
            <button style={btn(false)} onClick={exportCsv}>⬇ Export CSV</button>
          </>
        )}
      </div>
    </div>
  );
}
